'use client'

import { useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export interface NotificationPrefs {
  critical_channel: 'default' | 'web' | 'email' | 'mobile'
  important_channel: 'default' | 'web' | 'email' | 'off'
  digest_channel: 'email' | 'off'
  digest_schedule: 'daily' | 'weekly_monday' | 'off'
  quiet_hours_start: string | null
  quiet_hours_end: string | null
}

type SaveStatus = 'idle' | 'success' | 'error'

const selectTriggerClass =
  'h-10 w-full rounded-card border-[0.5px] border-line bg-surface px-3 text-[13.5px] text-text-strong shadow-none'

export function NotificationPreferencesForm({ initialPrefs }: { initialPrefs: NotificationPrefs }) {
  const [prefs, setPrefs] = useState<NotificationPrefs>(initialPrefs)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState<SaveStatus>('idle')

  async function handleSave() {
    setSaving(true)
    setSaved('idle')

    try {
      const res = await fetch('/api/notifications/preferences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(prefs),
      })
      setSaved(res.ok ? 'success' : 'error')
    } catch {
      setSaved('error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-full bg-canvas text-text-strong">
      <div className="mx-auto w-full max-w-2xl px-4 py-10 sm:px-8">
        <header>
          <h1 className="m-0 text-[28px] font-bold tracking-[-0.02em] text-text-strong sm:text-[34px]">
            Notification preferences
          </h1>
          <p className="mt-1 max-w-[520px] text-[13.5px] leading-[1.5] text-text">
            Control how Atrium reaches out when something needs your attention.
          </p>
        </header>

        <section className="mt-8 space-y-4">
          <PreferenceField
            label="Critical alerts"
            description="Connection failures, critical budgets, and frozen Smart Spend - things that break flows."
          >
            <Select
              value={prefs.critical_channel}
              onValueChange={value =>
                setPrefs({
                  ...prefs,
                  critical_channel: value as NotificationPrefs['critical_channel'],
                })
              }
              disabled={saving}
            >
              <SelectTrigger className={selectTriggerClass} aria-label="Critical alerts channel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default (in-app)</SelectItem>
                <SelectItem value="web">Web push</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="mobile">Mobile push</SelectItem>
              </SelectContent>
            </Select>
          </PreferenceField>

          <PreferenceField
            label="Important alerts"
            description="Schedule health, stuck onboarding, and goal misses - things you should know."
          >
            <Select
              value={prefs.important_channel}
              onValueChange={value =>
                setPrefs({
                  ...prefs,
                  important_channel: value as NotificationPrefs['important_channel'],
                })
              }
              disabled={saving}
            >
              <SelectTrigger className={selectTriggerClass} aria-label="Important alerts channel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default (in-app)</SelectItem>
                <SelectItem value="web">Web push</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="off">Off</SelectItem>
              </SelectContent>
            </Select>
          </PreferenceField>

          <PreferenceField
            label="Digest emails"
            description="Summary of alerts from the last day or week."
          >
            <Select
              value={prefs.digest_schedule}
              onValueChange={value =>
                setPrefs({
                  ...prefs,
                  digest_schedule: value as NotificationPrefs['digest_schedule'],
                  digest_channel: value === 'off' ? 'off' : 'email',
                })
              }
              disabled={saving}
            >
              <SelectTrigger className={selectTriggerClass} aria-label="Digest email schedule">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Never</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly_monday">Weekly (Mondays)</SelectItem>
              </SelectContent>
            </Select>
          </PreferenceField>

          <PreferenceField
            label="Quiet hours"
            description="Suppress non-critical pushes during this window in your local time."
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                type="time"
                value={prefs.quiet_hours_start ?? ''}
                onChange={event =>
                  setPrefs({ ...prefs, quiet_hours_start: event.target.value || null })
                }
                disabled={saving}
                className="h-10 rounded-card border-[0.5px] border-line bg-surface px-3 text-[13.5px] text-text-strong outline-none focus:border-brand disabled:opacity-60"
                aria-label="Quiet hours start"
              />
              <span className="text-[13px] text-text-muted">to</span>
              <input
                type="time"
                value={prefs.quiet_hours_end ?? ''}
                onChange={event =>
                  setPrefs({ ...prefs, quiet_hours_end: event.target.value || null })
                }
                disabled={saving}
                className="h-10 rounded-card border-[0.5px] border-line bg-surface px-3 text-[13.5px] text-text-strong outline-none focus:border-brand disabled:opacity-60"
                aria-label="Quiet hours end"
              />
            </div>
          </PreferenceField>
        </section>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save preferences'}
          </Button>
          {saved === 'success' && (
            <span className="text-[13px] font-medium text-green-600">Saved</span>
          )}
          {saved === 'error' && (
            <span className="text-[13px] font-medium text-red-600">Save failed. Try again.</span>
          )}
        </div>

        <footer className="mt-10 border-t border-line pt-5 text-[12px] leading-[1.5] text-text-muted">
          Critical alerts always reach you somehow. Quiet hours do not suppress them. Email
          digests are not sent yet.
        </footer>
      </div>
    </div>
  )
}

function PreferenceField({
  label,
  description,
  children,
}: {
  label: string
  description: string
  children: ReactNode
}) {
  return (
    <div className="rounded-card border-[0.5px] border-line bg-surface p-4">
      <label className="block text-[13px] font-semibold text-text-strong">{label}</label>
      <p className="mb-3 mt-1 text-[12.5px] leading-[1.45] text-text-muted">{description}</p>
      {children}
    </div>
  )
}
